import { resolveGoalEdit, withPendingGoal } from "./habitWeeklyGoal";
import { unitOf, clampGoal, UNIT_LABEL_MAX } from "./habitUnits";
import { intervalUnitOf, intervalEveryOf } from "./habitInterval";
import { todayStr } from "./dateHelpers";

// Shared clean-up for every habit form save path (HabitsTab add/edit and
// App.jsx's edit-from-Clock-In). The form merges into the habit object
// ({...h, ...form}), so anything here would otherwise be persisted.
//
// - `_iconMode` / `_openPicker` are UI-only flags for the emoji/icon picker;
//   `_unitPickerOpen` is the Custom unit pop-up (UnitPicker) being open.
// - `reminderOffTime` remembers a reminder switched off from the habit's
//   detail panel, so it can be switched back on. Once the reminder time is
//   changed in the form (set to a new time, or cleared), that remembered
//   time is stale and is dropped.
//
// - `_goal*` are the "Start a new goal" switches' state (goalFormFlags; UI only).
// - A changed goal number is NOT saved over the current one: it is parked in
//   `goalPending` and takes over at the start of the next period, so past weeks
//   keep the goal they were judged by (see resolveGoalEdit).
export function cleanHabitForm(form, prev = null, today = undefined) {
  const { _iconMode, _openPicker, _unitPickerOpen, _goalNote, _goalNewDaily, _goalNewFreq, _goalNowDaily, _goalNowFreq, _goalNextFrom, _goalShape, _goalOrig, ...picked } = form || {};
  // Before the goal logic, so a frequency kept as it was isn't read as a type switch.
  const rest = normalizeHabitInterval(normalizeHabitUnit(keepFrequencyIfNoDays(picked, prev)), prev, today);
  if (prev && (rest.reminderTime || "") !== (prev.reminderTime || "")) rest.reminderOffTime = undefined;
  if (prev) Object.assign(rest, resolveGoalEdit(rest, prev, today, { daily: !!_goalNewDaily, freq: !!_goalNewFreq, dailyNow: !!_goalNowDaily, freqNow: !!_goalNowFreq }));
  return rest;
}

// ── Interval habits (habitInterval.js) ──────────────────────────────────────
// Keeps N inside its unit's range and stamps `intervalStart`, the day the
// rhythm counts from when the habit has no start date. Kept from the habit's
// earlier save so editing doesn't move the schedule; only a change of start
// date does (habitStartDate wins over it). Other frequencies pass through.
export function normalizeHabitInterval(form, prev = null, today = undefined) {
  // A To Do interval, or a Limited habit's "not allowed every N" days (Jade, 2026-10-17).
  const limitedInterval = form && form.habitType === "limited" && form.limitedInterval && !form.allowanceMode;
  if (!form || (form.frequencyType !== "interval" && !limitedInterval)) return form;
  const out = { ...form, intervalUnit: intervalUnitOf(form), intervalEvery: intervalEveryOf(form) };
  if (!out.intervalStart) out.intervalStart = (prev && prev.intervalStart) || today || todayStr();
  return out;
}

// ── Units (habitUnits.js) ───────────────────────────────────────────────────
// The goal is kept inside the unit's range, a custom unit's name is trimmed,
// and a unit other than Custom carries no name (so a stale one can't read as a
// goal change later). An amount habit (anything but Count) is logged once a
// day, not N times, so it keeps at most one time-of-day slot — the form caps
// the slots by dailyCount, which for 10,000 steps would mean 10,000 slots.
// Forms without unit fields pass through untouched.
export function normalizeHabitUnit(form) {
  if (!form || form.unit === undefined) return form;
  if ((form.habitType || "todo") !== "todo") {
    // Switched to a restricted habit: it has no unit, so drop an amount goal
    // (10,000 steps must not turn into "10,000 times" anywhere).
    return unitOf(form) === "count" ? form : { ...form, unit: "count", unitLabel: "", dailyCount: 1 };
  }
  const unit = unitOf(form);
  const out = { ...form, unit, dailyCount: clampGoal(unit, form.dailyCount) };
  out.unitLabel = unit === "custom" ? String(form.unitLabel || "").trim().slice(0, UNIT_LABEL_MAX) : "";
  if (unit !== "count" && Array.isArray(out.timeOfDay) && out.timeOfDay.length > 1) out.timeOfDay = out.timeOfDay.slice(0, 1);
  return out;
}

// ── A frequency with no days picked ─────────────────────────────────────────
// Tapping "Weekly" (or Monthly/Yearly on dates) switches the form to a shape
// that needs days, but picks none. Saved like that, the habit is due on no
// day at all and silently drops off Today (Jade, 2026-10-09: she tapped
// Weekly, picked nothing and saved, and a Daily habit vanished).
export function hasNoDaysPicked(h) {
  if (!h || (h.habitType || "todo") !== "todo") return false; // restricted habits don't use these
  const ft = h.frequencyType;
  if (ft === "specific") return (h.dayLabels || "named") === "named" && !(h.days || []).length;
  if (ft === "monthly") return h.monthlyMode !== "count" && !(h.monthDates || []).length;
  if (ft === "yearly") return (h.yearlyMode || "count") === "dates" && !(h.yearDates || []).length;
  return false;
}

// The frequency fields, kept or restored together.
const FREQUENCY_FIELDS = ["frequencyType", "dayLabels", "days", "monthlyMode", "monthDates", "weeklyCount", "monthlyCount", "yearlyMode", "yearDates", "yearlyCount", "intervalEvery", "intervalUnit", "intervalStart"];

// The save-time guard: a form left on such a shape keeps the habit's own
// frequency (the edit form's starting point, pending goal included, so nothing
// reads as a change), and a new habit stays Daily, the form's default.
export function keepFrequencyIfNoDays(form, prev = null) {
  if (!hasNoDaysPicked(form)) return form;
  if (!prev) return { ...form, frequencyType: "daily" };
  const was = withPendingGoal(prev);
  const out = { ...form };
  for (const k of FREQUENCY_FIELDS) {
    if (was[k] === undefined) delete out[k]; // the save merges over the habit, which keeps its own
    else out[k] = Array.isArray(was[k]) ? [...was[k]] : was[k];
  }
  return out;
}

// Load-time repair for habits already saved that way before the guard
// existed: due on no day, so they can't be seen or opened from Today. They go
// back to Daily, the form's default. Same array back when nothing needed it.
// Monthly ones are left alone: Today still shows them (isHabitDueToday), so
// they never vanished, and quietly making one Daily would be a surprise.
const vanished = (h) => hasNoDaysPicked(h) && h.frequencyType !== "monthly";
export function repairNoDayHabits(habits) {
  if (!Array.isArray(habits) || !habits.some(vanished)) return habits;
  return habits.map(h => (vanished(h) ? { ...h, frequencyType: "daily" } : h));
}

// Daily, specific-days and interval habits are the ones whose reminder repeats, so
// they're the ones the detail panel's on/off switch applies to.
export function canToggleHabitReminder(h) {
  const ft = h?.frequencyType || "daily";
  return ft === "daily" || ft === "specific" || ft === "interval";
}

// On/off from the detail panel. Off does exactly what the form's Clear
// button does (empties reminderTime/reminderDate — every alarm/notification
// collector, local and server, gates on reminderTime) and parks the time in
// reminderOffTime so On can restore it.
export function setHabitReminderOn(h, on) {
  if (on) {
    if (h.reminderTime || !h.reminderOffTime) return h;
    return { ...h, reminderTime: h.reminderOffTime, reminderOffTime: undefined };
  }
  if (!h.reminderTime) return h;
  return { ...h, reminderOffTime: h.reminderTime, reminderTime: "", reminderDate: "" };
}
